import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sparkles, UserPlus, AlertTriangle, CheckCircle, Clock, Shield, BarChart3 } from 'lucide-react';
import api from '../../lib/api';

const SCORE_COLOR = (score) => {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-yellow-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

const DIMENSION_ICONS = {
  availability: Clock,
  compliance: Shield,
  workload: BarChart3,
  history: CheckCircle,
};

export default function StaffSuggestionsPanel({ sessionId, onSelectStaff, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchSuggestions = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await api.get(`/smart-roster/suggestions/${sessionId}`);
      if (res.data?.data) setData(res.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchSuggestions(); }, [sessionId, refreshKey]);

  const handleSelect = async (suggestion, rank) => {
    try {
      await api.post('/smart-roster/log-selection', {
        session_id: sessionId,
        staff_id: suggestion.staff_id,
        was_suggested: true,
        suggestion_rank: rank + 1,
        suggestion_score: suggestion.total_score,
      });
    } catch (e) { /* audit log is best-effort */ }

    if (onSelectStaff) {
      onSelectStaff(suggestion.staff_id);
    }
  };

  if (!sessionId) return null;

  return (
    <Card className="bg-[#0d0d14] border-[#1a1a2e]" data-testid="staff-suggestions-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-400" />
          <CardTitle className="text-sm text-slate-300">AI Staff Suggestions</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchSuggestions}
          disabled={loading}
          className="text-xs text-slate-500 hover:text-slate-300"
          data-testid="refresh-suggestions-btn"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {loading && !data && (
          <div className="py-4 text-center text-slate-600 text-xs">Analyzing staff matches...</div>
        )}

        {data && data.suggestions?.length === 0 && (
          <div className="py-3 text-center text-slate-600 text-xs">No additional staff candidates available</div>
        )}

        {data?.shortfall > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded bg-red-900/20 border border-red-900/30 text-xs text-red-400" data-testid="shortfall-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>Staff shortfall: need {data.shortfall} more</span>
          </div>
        )}

        {data?.suggestions?.map((s, idx) => (
          <div
            key={s.staff_id}
            className={`rounded border transition-colors ${
              s.has_conflicts
                ? 'border-red-900/40 bg-red-950/20'
                : s.is_recommended
                  ? 'border-emerald-900/40 bg-emerald-950/10'
                  : 'border-[#1a1a2e] bg-[#12121a]'
            }`}
            data-testid={`suggestion-${idx}`}
          >
            <div
              className="flex items-center gap-2 px-3 py-2 cursor-pointer"
              onClick={() => setExpanded(expanded === idx ? null : idx)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${SCORE_COLOR(s.total_score)}`}>{s.total_score}</span>
                  <span className="text-xs text-slate-500">/100</span>
                  <span className="text-sm text-slate-200 truncate">{s.staff_name}</span>
                </div>
                {s.warnings?.length > 0 && (
                  <p className="text-[10px] text-orange-400 mt-0.5 truncate">{s.warnings[0]}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {s.is_recommended && (
                  <Badge className="bg-emerald-900/50 text-emerald-300 text-[10px] px-1.5">Recommended</Badge>
                )}
                {s.has_conflicts && (
                  <Badge className="bg-red-900/50 text-red-300 text-[10px] px-1.5">Conflict</Badge>
                )}
                {!s.has_conflicts && onSelectStaff && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-blue-400 hover:bg-blue-900/30"
                    onClick={(e) => { e.stopPropagation(); handleSelect(s, idx); }}
                    data-testid={`select-staff-${idx}`}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {expanded === idx && (
              <div className="px-3 pb-2 space-y-1.5 border-t border-[#1a1a2e] pt-2">
                {Object.entries(s.scores).map(([dim, score]) => {
                  const Icon = DIMENSION_ICONS[dim] || CheckCircle;
                  const maxScores = { availability: 40, compliance: 25, workload: 20, history: 15 };
                  const pct = Math.round((score / (maxScores[dim] || 1)) * 100);
                  return (
                    <div key={dim} className="flex items-center gap-2 text-xs">
                      <Icon className="h-3 w-3 text-slate-500 shrink-0" />
                      <span className="text-slate-500 w-20 capitalize">{dim}</span>
                      <div className="flex-1 h-1.5 bg-[#1a1a2e] rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-slate-400 w-16 text-right">{s.reasons[dim]}</span>
                    </div>
                  );
                })}
                {s.email && <p className="text-[10px] text-slate-600 mt-1">{s.email}</p>}
              </div>
            )}
          </div>
        ))}

        {data && (
          <p className="text-[10px] text-slate-600 text-center pt-1">
            {data.total_candidates} candidates analyzed
          </p>
        )}
      </CardContent>
    </Card>
  );
}
